"use client";

import { Translate } from "@/components/Translate";
import { Divider } from "@/components/ui/Divider";
import {
	DrawerClose,
	DrawerContent,
	DrawerHeader,
	DrawerMain,
	DrawerRoot,
	DrawerTitle,
	DrawerViewport,
} from "@/components/ui/Drawer";
import { useUrlSearchParams } from "@frend-digital/ui/next";
import React, { Suspense, useCallback, type ComponentProps } from "react";
import { LoginForm } from "./Login/LoginForm";
import { RegisterContentArea, RegisterForm } from "./Register/RegisterForm";
import { Slot } from "@/components/ui/Slot";
import { usePathname } from "@/i18n/routing";
import type { StoryblokRichtext } from "@/storyblok-base";
import { useQuery } from "@tanstack/react-query";
import { rpc } from "#rpc";
import { useLang } from "@/i18n/client";
import { StoryblokComponentList } from "@frend-digital/storyblok";
import styles from "./Account.module.css";
import { Flex } from "@/components/ui/Flex";
import { AutoAnimate } from "@/components/ui/AutoAnimate";

export const useAccountModal = () => {
	const pathname = usePathname();
	const searchParams = useUrlSearchParams();
	const mode = searchParams.get("accountModal") as "login" | "register" | undefined;

	const isLogin = pathname.includes("/account/login");

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	const setOpen = useCallback(
		(open: boolean, mode?: "login" | "register") => {
			if (open && !isLogin) {
				searchParams.set("accountModal", mode || "login");
			} else {
				searchParams.delete("accountModal");
			}
		},
		[isLogin],
	);

	return [!!mode, setOpen, mode || undefined] as const;
};

export const AccountModal = ({
	isRequired,
	signupTerms,
	signupLabel,
}: { isRequired?: boolean; signupTerms?: StoryblokRichtext | undefined; signupLabel?: string }) => {
	const [open, setOpen, mode] = useAccountModal();
	const language = useLang();

	const { data } = useQuery({
		queryKey: ["account-story", language],
		queryFn: async () => {
			const result = await rpc.stories.getAccount(language);

			return result;
		},
		enabled: open,
		staleTime: 1000 * 5 * 60,
	});

	return (
		<DrawerRoot open={open} onOpenChange={(open) => setOpen(open, mode)}>
			<DrawerViewport lock>
				<DrawerContent>
					<DrawerHeader>
						<Flex justify="between" align="center">
							<DrawerTitle>
								<Translate>{mode === "register" ? "Register" : "Login"}</Translate>
							</DrawerTitle>
							<DrawerClose static />
						</Flex>
					</DrawerHeader>
					<Divider />
					<AutoAnimate asChild>
						<DrawerMain>
							{mode === "register" ? (
								<>
									<RegisterForm modal signUpTerms={signupTerms} signupLabel={signupLabel} />
									<div className={styles.contentArea}>
										<RegisterContentArea>
											{data?.content.registerContent && (
												<StoryblokComponentList
													small
													bloks={data?.content.registerContent}
													preview
												/>
											)}
										</RegisterContentArea>
									</div>
								</>
							) : (
								<>
									<LoginForm modal />
									<div className={styles.contentArea}>
										{data?.content.loginContent && (
											<StoryblokComponentList small bloks={data?.content.loginContent} preview />
										)}
									</div>
								</>
							)}
						</DrawerMain>
					</AutoAnimate>
				</DrawerContent>
			</DrawerViewport>
		</DrawerRoot>
	);
};

const UnsuspendedAccountModalTrigger = ({
	mode,
	asChild,
	...rest
}: ComponentProps<"button"> & {
	mode: "login" | "register";
	asChild?: boolean;
}) => {
	const [, setOpen] = useAccountModal();
	const Component = asChild ? Slot : "button";
	return <Component {...rest} onClick={() => setOpen(true, mode)} />;
};

export const AccountModalTrigger: typeof UnsuspendedAccountModalTrigger = (props) => {
	return (
		<Suspense fallback={props.asChild ? <>{props.children}</> : null}>
			<UnsuspendedAccountModalTrigger {...props} />
		</Suspense>
	);
};
